import React, { useEffect, useRef } from 'react';
import '../styles/components/ConfirmDialog.css';

// Generic confirmation dialog for destructive actions (Remove all downloads,
// Clear queue, ...). Every piece of text arrives as a prop already
// translated by the caller, so this component has no i18n dependency of its
// own and stays trivially testable in isolation.
//
// Escape and a backdrop click both cancel (same click-outside-to-close
// convention as SettingsSheet's overlay), and focus moves onto the dialog
// itself on open — a destructive-action confirmation must be operable from
// the keyboard the moment it appears, without relying on the caller to have
// moved focus there already.
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="confirm-overlay" onClick={onCancel}>
      <div
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="confirm-title">{title}</h2>
        <p className="confirm-message">{message}</p>
        <div className="confirm-actions">
          <button type="button" className="confirm-cancel-btn" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" className="confirm-confirm-btn" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
